import { Link } from "react-router-dom";
import { QrCode, Smartphone, KeyRound, Building2, ShieldCheck, Printer } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../components/ui/accordion";

const USER_STEPS = [
  [QrCode, "Scannez le QR code", "Affiché au guichet, en agence, sur un reçu ou une affiche. Il ouvre directement le formulaire de la bonne agence et du bon service."],
  [Smartphone, "Remplissez le formulaire", "Note de 1 à 5, catégorie, commentaire, date de l'expérience et pièces jointes si besoin. Aucun compte n'est nécessaire."],
  [KeyRound, "Conservez vos codes", "Vous recevez un numéro de référence et un code secret à 6 chiffres pour suivre votre dossier et répondre à l'organisation."],
];
const ORG_STEPS = [
  [Building2, "Structurez votre espace", "Créez vos agences et services pour que chaque retour arrive à la bonne équipe."],
  [Printer, "Générez et imprimez", "Un QR code par guichet, agence ou service, téléchargeable et imprimable en un clic."],
  [ShieldCheck, "Traitez et répondez", "Analyse IA, réponses, demandes de précision, modération des avis publics."],
];
const FAQ = [
  ["Mes coordonnées sont-elles publiées ?", "Non. Les coordonnées personnelles ne sont jamais publiées. Seuls les avis publics validés par l'organisation sont visibles, avec un nom d'auteur neutre (sauf si vous choisissez d'afficher votre prénom dans vos préférences)."],
  ["Quelle différence entre avis public et message privé ?", "Un avis public peut être publié sur la fiche de l'organisation après validation. Un message privé (réclamation, signalement) n'est lu que par l'organisation."],
  ["J'ai perdu mon code secret", "Pour des raisons de sécurité, le code secret n'est affiché qu'une seule fois et n'est pas récupérable. Créez un compte pour retrouver vos prochains dépôts directement dans votre espace."],
  ["Le QR code ne fonctionne pas", "Il a peut-être été désactivé par l'organisation. Vous pouvez la retrouver dans l'annuaire et déposer votre retour depuis sa fiche."],
];

const Steps = ({ title, steps }) => (
  <div>
    <h2 className="font-display text-xl font-bold">{title}</h2>
    <ol className="mt-6 space-y-6">
      {steps.map(([Icon, t, d], i) => (
        <li key={t} className="flex gap-4">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-blue-600 text-white"><Icon className="h-4 w-4" /></span>
          <div><p className="font-medium"><span className="mr-2 font-mono text-xs text-slate-400">0{i + 1}</span>{t}</p><p className="mt-1 text-sm text-slate-600">{d}</p></div>
        </li>
      ))}
    </ol>
  </div>
);

export default function HowItWorks() {
  return (
    <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 lg:px-8">
      <p className="eyebrow">Fonctionnement</p>
      <h1 className="mt-3 max-w-3xl font-display text-3xl font-bold tracking-tight sm:text-4xl">La collecte par QR code, en moins d'une minute.</h1>
      <div className="mt-12 grid gap-12 lg:grid-cols-2">
        <div className="card-surface p-6 sm:p-8"><Steps title="Pour les usagers" steps={USER_STEPS} /></div>
        <div className="card-surface p-6 sm:p-8"><Steps title="Pour les organisations" steps={ORG_STEPS} /></div>
      </div>
      <div className="mt-16 grid gap-10 lg:grid-cols-3">
        <h2 className="font-display text-2xl font-bold">Questions fréquentes</h2>
        <Accordion type="single" collapsible className="lg:col-span-2" data-testid="faq-accordion">
          {FAQ.map(([q, a], i) => (
            <AccordionItem key={q} value={`q${i}`}><AccordionTrigger data-testid={`faq-item-${i}`}>{q}</AccordionTrigger><AccordionContent className="text-slate-600">{a}</AccordionContent></AccordionItem>
          ))}
        </Accordion>
      </div>
      <div className="mt-16 flex flex-wrap gap-3">
        <Button asChild className="rounded-full" data-testid="how-directory-button"><Link to="/entreprises">Trouver une organisation</Link></Button>
        <Button asChild variant="outline" className="rounded-full" data-testid="how-track-button"><Link to="/suivi">Suivre un dossier</Link></Button>
      </div>
    </div>
  );
}
